import { fetchPopularSearches } from "@/services/GalleriesService";
import { useEffect, useRef, useState } from "react";
import CustomSpinner from "../CustomSpinner/CustomSpinner";
import { GalleryI } from "@/interfaces/galleries/Galleries";
const linkArrow = "../../assets/images/icons/arrow-left.svg";
import { useGallerySearch } from "@/hooks/useGallerySearch";
import { nameSlug } from "@/utils/Functions/NameSlug";
import { FaSearch } from "react-icons/fa";
import { useRouter } from "next/navigation";
import { useThemeContext } from "@/context/ThemeContext";

interface SearchBarPropsI {
  show: boolean;
}

function SearchBar(props: SearchBarPropsI) {
  const { show } = props;
  const { theme } = useThemeContext();
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [popularSearches, setPopularSearches] = useState<Array<GalleryI>>([]);
  const router = useRouter();
  const {
    searchText,
    setSearchText,
    debouncedText,
    isSearching,
    galleries,
    loadingSearch,
    loadingMore,
    hasMore,
    loadMoreRef,
    resetSearch,
  } = useGallerySearch();

  const SearchRef: any = useRef(null);

  const handleChange = (e: any) => {
    setSearchText(e.target.value);
  };

  const getPopularSearches = async () => {
    fetchPopularSearches().then((res) => {
      if (res && res.status === "Success") {
        setPopularSearches(res.data);
      }
    });
  };

  const handleNav = (galleryName: string) => {
    router.push("/" + nameSlug(galleryName));
    setShowSearch(false);
    resetSearch();
  };

  useEffect(() => {
    getPopularSearches();
  }, []);

  useEffect(() => {
    const handleOutsideClick = (event: any) => {
      if (SearchRef.current && SearchRef.current.contains(event.target)) {
        setShowSearch(true);
      } else if (
        SearchRef.current &&
        !SearchRef.current.contains(event.target)
      ) {
        setShowSearch(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  return (
    <div
      ref={SearchRef}
      className={
        " search-container md:relative  w-full " +
        (show ? "max-md:flex" : "max-md:hidden")
      }
    >
      <div className="flex w-full bg-white overflow-hidden border border-black -skew-x-[12deg] px-1.5 sm:px-2 rounded-sm">
        <div className="skew-x-[12deg]  w-full">
          <input
            id="search"
            type="text"
            placeholder="Search"
            value={searchText}
            onChange={handleChange}
            className="w-full py-3 pl-3 sm:pl-4 pr-10 text-black focus:outline-none"
          />
          <label
            htmlFor="search"
            className="absolute right-4 sm:right-5 top-1/2 -translate-y-1/2 cursor-pointer"
          >
            <FaSearch className="text-black text-xl sm:text-2xl" />
          </label>
        </div>
      </div>
      <div
        className={`w-full max-md:left-2 shadow-lg shadow-black/25 absolute max-md:bottom-[44px] rounded-t-xl md:rounded-b-xl p-4  bg-black-dark right-2 ${
          showSearch ? "flex" : "hidden"
        } ${theme === "light" ? " bg-white " : "bg-black-dark"}`}
      >
        {loadingSearch && <CustomSpinner size={8} borderWidth={6} />}
        {!loadingSearch && isSearching && galleries.length === 0 && (
            <div className="w-full flex flex-col">
              <h3 style={{ fontSize: 16 }} className="text-center">
                Sorry, we couldn’t find any results matching
              </h3>
              <h3
                className="font-semibold text-center text-sm"
                style={{ fontSize: 14 }}
              >
                '{debouncedText}'
              </h3>
              <h4 className="text-start font-light">Try:</h4>
              <ul className="list-disc text-white pl-5 space-y-2">
                <li className="font-light text-start">
                  Searching again using more general terms
                </li>
                <li className="font-light text-start">
                  Exploring the catalog by category
                </li>
              </ul>
            </div>
          )}
        {!loadingSearch && !isSearching && (
          <div className="w-full gap-3 flex flex-col">
            <h3 style={{ fontSize: 16 }}>Popular Searches</h3>
            <div className="flex flex-wrap gap-4">
              {popularSearches.map((popular) => {
                return (
                  <div
                    className="w-fit p-3 rounded-xs flex gap-2 items-center bg-gray-fade border-2 border-purple-dark "
                    role="button"
                    onClick={() => handleNav(popular.name)}
                    key={popular._id}
                  >
                    <p style={{ fontSize: 12 }}>{popular?.name}</p>
                    <img src={linkArrow} className="rotate-135" />
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {!loadingSearch && galleries.length > 0 && (
          <div className="w-full gap-3 flex flex-col min-h-0">
            <h3 style={{ fontSize: 24 }} className=" underline">
              Galleries
            </h3>
            <h4 className="text-center">'{debouncedText.trim()}'</h4>
            <div className="flex flex-col gap-3 max-h-[50vh] overflow-y-auto pr-2">
              {galleries.map((gallery) => {
                return (
                  <div
                    className="flex flex-col"
                    role="button"
                    key={gallery._id}
                    onClick={() => handleNav(gallery.name)}
                  >
                    <h4 style={{ fontSize: 16 }}>{gallery.name}</h4>
                    <p style={{ fontSize: 12 }} className="opacity-40">
                      {gallery.photoGallery} available • Created by{" "}
                      {gallery.authorId && gallery.authorId.name
                        ? gallery.authorId.name
                        : "Account deleted"}
                    </p>
                  </div>
                );
              })}
              {hasMore && (
                <div ref={loadMoreRef} className="flex justify-center py-2">
                  {loadingMore && <CustomSpinner size={3} borderWidth={4} />}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchBar;
